import { useState, useEffect, useRef } from 'react';
import { NavIcon } from '../../../shell/shared';
import {
  type EncounterTabSettings,
  type LayoutConfig,
  type TabConfig,
  type GuidedNotePreset,
  DEFAULT_LAYOUT,
  PRESETS,
} from './useEncounterTabConfig';
import { GUIDED_NOTE_PRESETS, reorderTabsForPreset } from './guided-note-presets';

// ─── Props ───

interface Props {
  open: boolean;
  onClose: () => void;
  settings: EncounterTabSettings;
  onSave: (next: EncounterTabSettings) => void;
  onReset: () => void;
  primary: string;
  /**
   * Clinic-level Patient Consultation Tabs visibility (PRD.181).
   * When provided, sub-modules disabled at the clinic level are hidden from
   * this settings UI so it stays aligned with what the consultation
   * workspace actually renders. `null` / undefined = no clinic constraint.
   */
  clinicVisibleConsultKeys?: Set<string> | null;
  /**
   * PRD.282 / PRD.300 — the clinic-type Guided Note preset. `settings` already
   * arrives clinic-derived when the user has no override; this is the fallback
   * for a payload that predates the layout field. Defaults to 'general'.
   */
  defaultGuidedNotePreset?: GuidedNotePreset;
}

// ─── Styles ───

const RADIO_BTN = (active: boolean, primary: string): React.CSSProperties => ({
  flex: 1,
  height: 36,
  border: active ? `2px solid ${primary}` : '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  background: active ? `${primary}08` : 'var(--bg-surface)',
  color: active ? primary : 'var(--text-muted)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: active ? 600 : 500,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
});

const SECTION_LABEL: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: 'var(--text-muted)',
  marginBottom: 8,
};

const TOGGLE_ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 0',
  borderBottom: '1px solid var(--border)',
};

// ─── Component ───

export function VisitNoteSettingsDrawer({
  open,
  onClose,
  settings,
  onSave,
  onReset,
  primary,
  clinicVisibleConsultKeys,
  defaultGuidedNotePreset = 'general',
}: Props) {
  const [layout, setLayout] = useState<LayoutConfig>(
    () => settings.layout ?? { ...DEFAULT_LAYOUT, guidedNotePreset: defaultGuidedNotePreset },
  );
  const [tabs, setTabs] = useState<TabConfig[]>(() => [...settings.tabs]);
  const [activePreset, setActivePreset] = useState<string | null>(settings.preset);
  // PRD.282 — per-guided-preset sub-module visibility (Guided layout only). Each
  // discipline keeps its own on/off set; edits here never touch the shared
  // Tabs/Accordion `tabs`.
  const [guidedTabsByPreset, setGuidedTabsByPreset] = useState<
    Partial<Record<GuidedNotePreset, TabConfig[]>>
  >(() => settings.guidedTabsByPreset ?? {});

  // Sync local state when drawer opens
  const prevOpen = useRef(open);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setLayout(
        settings.layout ?? { ...DEFAULT_LAYOUT, guidedNotePreset: defaultGuidedNotePreset },
      );
      setTabs([...settings.tabs]);
      setActivePreset(settings.preset);
      setGuidedTabsByPreset(settings.guidedTabsByPreset ?? {});
    }
    prevOpen.current = open;
  }, [open, settings, defaultGuidedNotePreset]);

  const isGuided = layout.style === 'guided_note';
  const currentPreset = layout.guidedNotePreset;

  // The sub-module set the drawer edits: the active guided preset's own set in
  // Guided mode, otherwise the shared `tabs` array. An uncustomized discipline
  // falls back to `tabs` — which for a user on the clinic default IS the
  // clinic-derived selection (PRD.300), so the drawer shows what the workspace
  // actually renders rather than an all-on list.
  const activeTabs: TabConfig[] = isGuided ? (guidedTabsByPreset[currentPreset] ?? tabs) : tabs;

  const setActiveTabs = (updater: (prev: TabConfig[]) => TabConfig[]) => {
    if (isGuided) {
      setGuidedTabsByPreset((m) => ({
        ...m,
        [currentPreset]: updater(m[currentPreset] ?? tabs),
      }));
    } else {
      setTabs(updater);
    }
  };

  // Sub-modules disabled at the clinic level are hidden from this UI so
  // it stays consistent with the consultation workspace (PRD.181). Guided mode
  // also orders the list to match the discipline's flow.
  const clinicFilteredTabs = clinicVisibleConsultKeys
    ? activeTabs.filter((t) => clinicVisibleConsultKeys.has(t.key))
    : activeTabs;
  const displayedTabs = isGuided
    ? reorderTabsForPreset(clinicFilteredTabs, currentPreset)
    : clinicFilteredTabs;
  const visibleCount = displayedTabs.filter((t) => t.visible).length;
  const canSave = visibleCount > 0;

  const handleToggleTab = (key: string) => {
    setActiveTabs((prev) => prev.map((t) => (t.key === key ? { ...t, visible: !t.visible } : t)));
    // Role presets apply to the Tabs/Accordion `tabs`; clear it only there.
    if (!isGuided) setActivePreset(null);
  };

  const handleApplyPreset = (presetKey: string) => {
    const preset = PRESETS[presetKey];
    if (!preset) return;
    setTabs([...preset.tabs]);
    setActivePreset(presetKey);
  };

  const handleSave = () => {
    if (!canSave) return;
    onSave({
      version: 1,
      preset: activePreset,
      tabs,
      layout,
      guidedTabsByPreset,
    });
    onClose();
  };

  /**
   * PRD.300 §5.8 — Reset REMOVES the user's saved override so they follow the
   * live clinic default again. It deliberately does not write the current
   * default back: if the clinic changes its type later, users on default must
   * pick that up. Closes immediately — the drawer re-seeds from the
   * clinic-derived `settings` on its next open.
   */
  const handleReset = () => {
    onReset();
    onClose();
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.15)',
          zIndex: 998,
        }}
      />

      {/* Drawer — `pc-viewport-fill` (not height:100vh) so the Save/Reset
          footer stays inside the visible viewport on a tablet whose browser is
          showing its URL bar. */}
      <div
        className="pc-viewport-fill"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: 380,
          maxWidth: '100vw',
          background: 'var(--bg-surface)',
          borderLeft: '1px solid var(--border)',
          boxShadow: '-4px 0 12px rgba(0,0,0,0.08)',
          zIndex: 999,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: 'var(--sp-4) var(--sp-5)',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <h2
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
              margin: 0,
            }}
          >
            Visit Note Settings
          </h2>
          <button
            onClick={onClose}
            style={{
              width: 28,
              height: 28,
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              color: 'var(--text-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <NavIcon name="x" />
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: 'var(--sp-5)' }}>
          {/* Layout Style */}
          <div style={{ marginBottom: 'var(--sp-6)' }}>
            <div style={SECTION_LABEL}>Layout Style</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setLayout((l) => ({ ...l, style: 'tabs' }))}
                style={RADIO_BTN(layout.style === 'tabs', primary)}
              >
                <NavIcon name="layout-grid" /> Tabs
              </button>
              <button
                onClick={() => setLayout((l) => ({ ...l, style: 'accordion' }))}
                style={RADIO_BTN(layout.style === 'accordion', primary)}
              >
                <NavIcon name="list" /> Accordion
              </button>
              <button
                onClick={() => setLayout((l) => ({ ...l, style: 'guided_note' }))}
                style={RADIO_BTN(layout.style === 'guided_note', primary)}
              >
                <NavIcon name="file-text" /> Guided
              </button>
            </div>
          </div>

          {/* Guided Note Preset — only when guided_note selected */}
          {layout.style === 'guided_note' && (
            <div style={{ marginBottom: 'var(--sp-6)' }}>
              <div style={SECTION_LABEL}>Guided Note Preset</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {(
                  Object.entries(GUIDED_NOTE_PRESETS) as [
                    GuidedNotePreset,
                    (typeof GUIDED_NOTE_PRESETS)[GuidedNotePreset],
                  ][]
                ).map(([key, preset]) => {
                  const isActive = layout.guidedNotePreset === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setLayout((l) => ({ ...l, guidedNotePreset: key }))}
                      title={preset.description}
                      style={{
                        height: 30,
                        padding: '0 12px',
                        borderRadius: 'var(--radius-full)',
                        border: isActive ? `2px solid ${primary}` : '1px solid var(--border)',
                        background: isActive ? `${primary}08` : 'var(--bg-surface)',
                        color: isActive ? primary : 'var(--text-muted)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                    >
                      {preset.label}
                    </button>
                  );
                })}
              </div>
              <div
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text-muted)',
                  marginTop: 6,
                }}
              >
                {GUIDED_NOTE_PRESETS[layout.guidedNotePreset]?.description}
              </div>
            </div>
          )}

          {/* Sub-modules */}
          <div style={{ marginBottom: 'var(--sp-6)' }}>
            <div style={SECTION_LABEL}>Sub-modules</div>
            {displayedTabs.map((tab) => (
              <div key={tab.key} style={TOGGLE_ROW}>
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: tab.visible ? 'var(--text)' : 'var(--text-muted)',
                    fontWeight: tab.visible ? 500 : 400,
                  }}
                >
                  {tab.label}
                </span>
                <button
                  onClick={() => handleToggleTab(tab.key)}
                  style={{
                    width: 36,
                    height: 20,
                    borderRadius: 10,
                    border: 'none',
                    background: tab.visible ? primary : 'var(--border)',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'background 150ms',
                    padding: 0,
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      background: '#fff',
                      position: 'absolute',
                      top: 2,
                      left: tab.visible ? 18 : 2,
                      transition: 'left 150ms',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                    }}
                  />
                </button>
              </div>
            ))}
            {!canSave && (
              <div style={{ fontSize: 'var(--font-size-xs)', color: '#dc2626', marginTop: 6 }}>
                At least one module must be enabled.
              </div>
            )}
          </div>

          {/* Accordion Settings — only when accordion selected */}
          {layout.style === 'accordion' && (
            <div style={{ marginBottom: 'var(--sp-6)' }}>
              <div style={SECTION_LABEL}>Accordion Settings</div>

              {/* Mode */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <button
                  onClick={() => setLayout((l) => ({ ...l, accordionMode: 'single' }))}
                  style={RADIO_BTN(layout.accordionMode === 'single', primary)}
                >
                  Single open
                </button>
                <button
                  onClick={() => setLayout((l) => ({ ...l, accordionMode: 'multiple' }))}
                  style={RADIO_BTN(layout.accordionMode === 'multiple', primary)}
                >
                  Multiple open
                </button>
              </div>

              {/* Default open section */}
              <div style={{ marginBottom: 12 }}>
                <div
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-muted)',
                    marginBottom: 6,
                  }}
                >
                  Default open section
                </div>
                <select
                  value={layout.defaultOpenSection ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '__all__') {
                      if (layout.accordionMode === 'single') {
                        // Switch to multiple mode and open all
                        setLayout((l) => ({
                          ...l,
                          accordionMode: 'multiple',
                          defaultOpenSection: '__all__',
                        }));
                      } else {
                        setLayout((l) => ({ ...l, defaultOpenSection: '__all__' }));
                      }
                    } else {
                      setLayout((l) => ({ ...l, defaultOpenSection: val || null }));
                    }
                  }}
                  style={{
                    width: '100%',
                    height: 32,
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 'var(--font-size-sm)',
                    padding: '0 8px',
                    background: 'var(--bg-surface)',
                    color: 'var(--text)',
                  }}
                >
                  <option value="">None</option>
                  <option value="__all__">
                    All sections
                    {layout.accordionMode === 'single' ? ' (will switch to Multiple open)' : ''}
                  </option>
                  {displayedTabs
                    .filter((t) => t.visible)
                    .map((t) => (
                      <option key={t.key} value={t.key}>
                        {t.label}
                      </option>
                    ))}
                </select>
                {layout.defaultOpenSection === '__all__' && layout.accordionMode === 'single' && (
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-warning-text)',
                      marginTop: 4,
                    }}
                  >
                    "All sections" requires Multiple open mode. It will be enabled on save.
                  </div>
                )}
              </div>

              {/* Remember last open */}
              <div style={TOGGLE_ROW}>
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
                  Remember last open section
                </span>
                <button
                  onClick={() =>
                    setLayout((l) => ({ ...l, rememberLastOpen: !l.rememberLastOpen }))
                  }
                  style={{
                    width: 36,
                    height: 20,
                    borderRadius: 10,
                    border: 'none',
                    background: layout.rememberLastOpen ? primary : 'var(--border)',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'background 150ms',
                    padding: 0,
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      background: '#fff',
                      position: 'absolute',
                      top: 2,
                      left: layout.rememberLastOpen ? 18 : 2,
                      transition: 'left 150ms',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                    }}
                  />
                </button>
              </div>
            </div>
          )}

          {/* Presets */}
          <div style={{ marginBottom: 'var(--sp-6)' }}>
            <div style={SECTION_LABEL}>Presets</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {Object.entries(PRESETS).map(([key, preset]) => (
                <button
                  key={key}
                  onClick={() => handleApplyPreset(key)}
                  style={{
                    height: 30,
                    padding: '0 12px',
                    borderRadius: 'var(--radius-full)',
                    border:
                      activePreset === key ? `2px solid ${primary}` : '1px solid var(--border)',
                    background: activePreset === key ? `${primary}08` : 'var(--bg-surface)',
                    color: activePreset === key ? primary : 'var(--text-muted)',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: 'var(--sp-4) var(--sp-5)',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            gap: 8,
            flexShrink: 0,
          }}
        >
          <button
            onClick={handleReset}
            style={{
              flex: 1,
              height: 36,
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--bg-surface)',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Reset to Default
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            style={{
              flex: 1,
              height: 36,
              border: 'none',
              borderRadius: 'var(--radius-md)',
              background: canSave ? primary : 'var(--border)',
              color: '#fff',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: canSave ? 'pointer' : 'not-allowed',
            }}
          >
            Save
          </button>
        </div>
      </div>
    </>
  );
}
